import { getAccessToken } from '../authentication/supabaseService';
import { TranslatedText } from '../recipes/recipesService';

export type MepStockStatus = 'low' | 'out' | null;

export interface MepTaskComponent {
  componentId: string;            // UUID - primary identifier
  nameTranslations?: TranslatedText; // Name snapshot (used for custom free-text items)
  checked: boolean;
  checkedBy?: string;
  checkedAt?: string;
  stockStatus?: MepStockStatus;
}

// A free-text custom MEP item (standing task, not tied to a recipe)
export interface MepCustomItem {
  id: string;    // "custom-<uuid>"
  text: string;  // single free-text string; shown for all languages
}

export interface MepTaskItem {
  recipeId: string;
  menuItemName: string;
  components: MepTaskComponent[];
}

export interface MepTasks {
  tasksDate: string | null;
  items: MepTaskItem[];
}

export interface MepSection {
  id: string;
  restaurantId: string;
  sectionName: string;
  sectionNameTranslations: TranslatedText;
  menuItems: string[];
  customItems: MepCustomItem[];
  mepTasks: MepTasks;
  createdAt: Date;
  updatedAt: Date;
}

export interface MepSectionCreateRequest {
  restaurantId: string;
  sectionName: string;
  sectionNameTranslations: TranslatedText;
  menuItems: string[];
  customItems?: MepCustomItem[];
}

export interface MepSectionUpdateRequest {
  id: string;
  sectionName?: string;
  sectionNameTranslations?: TranslatedText;
  menuItems?: string[];
  customItems?: MepCustomItem[];
}

export async function fetchMepSectionsByRestaurant(
  restaurantId: string
): Promise<MepSection[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/mep/restaurant/${restaurantId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch MEP sections: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return data.map((item: any) => ({
    ...item,
    customItems: item.customItems || [],
    mepTasks: {
      tasksDate: item.mepTasks?.tasksDate || null,
      items: item.mepTasks?.items || [],
    },
    createdAt: new Date(item.createdAt),
    updatedAt: new Date(item.updatedAt),
  }));
}

export async function fetchMepSectionById(id: string): Promise<MepSection> {
  const token = await getAccessToken();
  const res = await fetch(`/api/mep/${id}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch MEP section: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return {
    ...data,
    customItems: data.customItems || [],
    mepTasks: {
      tasksDate: data.mepTasks?.tasksDate || null,
      items: data.mepTasks?.items || [],
    },
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
  };
}

export async function createMepSection(
  request: MepSectionCreateRequest
): Promise<MepSection> {
  const token = await getAccessToken();
  const res = await fetch('/api/mep/create', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    throw new Error(`Failed to create MEP section: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return {
    ...data,
    customItems: data.customItems || [],
    mepTasks: {
      tasksDate: data.mepTasks?.tasksDate || null,
      items: data.mepTasks?.items || [],
    },
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
  };
}

export async function updateMepSection(
  request: MepSectionUpdateRequest
): Promise<MepSection> {
  const token = await getAccessToken();
  const res = await fetch('/api/mep/update', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    throw new Error(`Failed to update MEP section: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return {
    ...data,
    customItems: data.customItems || [],
    mepTasks: {
      tasksDate: data.mepTasks?.tasksDate || null,
      items: data.mepTasks?.items || [],
    },
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
  };
}

export async function updateMepTasks(
  sectionId: string,
  mepTasks: MepTasks
): Promise<MepSection> {
  const token = await getAccessToken();
  const res = await fetch(`/api/mep/${sectionId}/tasks`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ mepTasks }),
  });
  if (!res.ok) {
    throw new Error(`Failed to update MEP tasks: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return {
    ...data,
    customItems: data.customItems || [],
    mepTasks: {
      tasksDate: data.mepTasks?.tasksDate || null,
      items: data.mepTasks?.items || [],
    },
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
  };
}

export async function completeMepSection(sectionId: string): Promise<MepSection> {
  const token = await getAccessToken();
  const res = await fetch(`/api/mep/${sectionId}/complete`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to complete MEP section: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return {
    ...data,
    customItems: data.customItems || [],
    mepTasks: {
      tasksDate: data.mepTasks?.tasksDate || null,
      items: data.mepTasks?.items || [],
    },
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
  };
}

export async function deleteMepSection(id: string): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/mep/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to delete MEP section: ${res.status} ${res.statusText}`);
  }
}
